import React, { useState } from "react";
import axios from "axios";
import { BsX, BsCheckCircleFill, BsBellSlashFill } from "react-icons/bs";
import { formatNumber } from "../utils/numbers";

const BASE = "https://eps.elkomprintama.com/";

function StockThresholdModal({ record, onClose, onSaved }) {
  const [value, setValue] = useState(
    record.min_stock != null ? String(record.min_stock) : "",
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const numVal = Number(String(value).trim());
  const invalid = value.trim() !== "" && (isNaN(numVal) || numVal < 0);
  const canSave = !saving && !saved && !invalid;

  const doSave = async (payload) => {
    setSaving(true);
    setError(null);
    try {
      await axios.patch(
        `${BASE}patch/api/store/min-stock/${record.product_id}`,
        { min_stock: payload },
      );
      setSaved(true);
      onSaved(record.product_id, payload);
      setTimeout(onClose, 800);
    } catch (e) {
      setError(e.response?.data?.msg || "Save failed — please try again.");
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (invalid) return;
    doSave(value.trim() === "" ? null : numVal);
  };

  const handleClear = () => doSave(null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-amber-600 rounded-t-2xl">
          <div>
            <h2 className="text-white font-bold text-base">
              Stock Alert Threshold
            </h2>
            <p className="text-amber-200 text-xs">
              Double-click any row to edit
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-amber-200 transition"
          >
            <BsX size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Product info */}
          <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
            <p className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-1">
              Product
            </p>
            <p className="text-sm font-semibold text-gray-800">
              {record.product_name}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {record.product_code} · Current stock:{" "}
              <span className="font-semibold text-gray-600">
                {formatNumber(record.current_stock)} {record.unit}
              </span>
            </p>
          </div>

          {/* Input */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Minimum Stock Threshold ({record.unit})
            </label>
            {record.min_stock != null && (
              <p className="text-xs text-amber-600 mb-1.5">
                Current: {formatNumber(record.min_stock)} {record.unit}
              </p>
            )}
            <input
              type="number"
              min="0"
              step="1"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 ${
                invalid
                  ? "border-red-400 bg-red-50 focus:ring-red-400 text-red-600"
                  : "border-gray-300 focus:ring-amber-400"
              }`}
              placeholder="e.g. 100"
            />
            {invalid && (
              <p className="text-xs text-red-500 mt-0.5">
                Must be a positive number
              </p>
            )}
            <p className="text-xs text-gray-400 mt-1.5">
              An alert will appear in the Dashboard after a stock-out brings
              stock at or below this value. Leave blank to disable.
            </p>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2">
              <p className="text-xs text-red-600">{error}</p>
            </div>
          )}

          {saved && (
            <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-3 py-2">
              <BsCheckCircleFill
                className="text-green-500 flex-shrink-0"
                size={14}
              />
              <p className="text-xs text-green-700 font-semibold">Saved!</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-6 py-4 border-t border-gray-100">
          {record.min_stock != null && (
            <button
              onClick={handleClear}
              disabled={saving || saved}
              title="Remove threshold"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 transition disabled:opacity-40"
            >
              <BsBellSlashFill size={12} /> Clear
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default StockThresholdModal;
